import type { CaregiverMinified } from '@api/READ_ONLY/caregiver_api/Api';
import type { Client } from '@api/READ_ONLY/client_api/Api';
import FormElement from '@app/components/ui/FormElement';
import CaregiversSelectGeneric from '@feat-caregivers/components/CaregiversSelect/CaregiversSelectGeneric';
import ClientSelectGeneric from '@feat-clients/components/ClientSelect/ClientSelectGeneric';
import useSmartErrorHandling from '@hooks/useSmartErrorHandling';
import { Alert, App, DatePicker, Input, InputNumber, Modal, Radio, Select } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createMovement } from './api';
import type {
  InventoryItem,
  InventoryMovementCreate,
  InventoryMovementType,
  InventoryRecipientType,
  RentalRateUnit,
} from './types';

export interface RecordMovementModalProps {
  open: boolean;
  item: InventoryItem | null;
  onClose: () => void;
  /** Called after a successful movement so the caller can refresh the list. */
  onSaved: () => void;
}

// rental_return is intentionally omitted: returns are recorded from the Rentals tab.
const MOVEMENT_TYPES: InventoryMovementType[] = ['purchase', 'sale', 'give', 'rental_out', 'adjustment'];
const RECIPIENT_TYPES: InventoryRecipientType[] = ['client', 'caregiver', 'external', 'internal'];
const RENTAL_RATE_UNITS: RentalRateUnit[] = ['day', 'week', 'month'];

const DATE_FORMAT = 'YYYY-MM-DD';

// Movement types that consume stock. Mirrors `stock_delta` in the backend's
// inventory/validation.py (and the inventory_stock_on_hand view): those are
// authoritative, this only saves the user a round-trip to be told no.
const CONSUMES_STOCK: InventoryMovementType[] = ['sale', 'give', 'rental_out'];

const RecordMovementModal = ({ open, item, onClose, onSaved }: RecordMovementModalProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { handleError } = useSmartErrorHandling();

  const [movementType, setMovementType] = useState<InventoryMovementType>('purchase');
  const [quantity, setQuantity] = useState<number | null>(null);
  const [unitCost, setUnitCost] = useState<number | null>(null);
  const [supplier, setSupplier] = useState('');
  const [unitPrice, setUnitPrice] = useState<number | null>(null);
  const [rentalRate, setRentalRate] = useState<number | null>(null);
  const [rentalRateUnit, setRentalRateUnit] = useState<RentalRateUnit>('day');
  const [rentalStart, setRentalStart] = useState<Dayjs | null>(null);
  const [rentalExpectedReturn, setRentalExpectedReturn] = useState<Dayjs | null>(null);
  const [recipientType, setRecipientType] = useState<InventoryRecipientType>('client');
  const [recipientId, setRecipientId] = useState<number | null>(null);
  const [recipientName, setRecipientName] = useState('');
  // The picked entity is kept alongside its id so the select can render its
  // label without refetching (GenericSearchSelect's selectedItemsProvider).
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedCaregiver, setSelectedCaregiver] = useState<CaregiverMinified | null>(null);
  const [visitId, setVisitId] = useState<number | null>(null);
  const [adjustmentSign, setAdjustmentSign] = useState<number | null>(1);
  const [notes, setNotes] = useState('');
  const [occurredAt, setOccurredAt] = useState<Dayjs | null>(null);

  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Seed the form whenever the modal opens: reset every field and prefill the
  // pricing inputs from the item's configured defaults where available.
  useEffect(() => {
    if (!open) return;
    setMovementType('purchase');
    setQuantity(null);
    setUnitCost(item?.defaultCost ?? null);
    setSupplier('');
    setUnitPrice(item?.defaultSalePrice ?? null);
    setRentalRate(item?.defaultRentalRate ?? null);
    setRentalRateUnit(item?.defaultRentalRateUnit ?? 'day');
    setRentalStart(null);
    setRentalExpectedReturn(null);
    setRecipientType('client');
    setRecipientId(null);
    setRecipientName('');
    setSelectedClient(null);
    setSelectedCaregiver(null);
    setVisitId(null);
    setAdjustmentSign(1);
    setNotes('');
    setOccurredAt(null);
    setError('');
  }, [open, item]);

  const showsRecipient = movementType === 'sale' || movementType === 'give' || movementType === 'rental_out';
  const showsChargeInfo =
    (movementType === 'sale' || movementType === 'rental_out') && showsRecipient && recipientType === 'client';

  // A negative adjustment writes stock off, so it is capped like a sale.
  const drawsDownStock =
    CONSUMES_STOCK.includes(movementType) || (movementType === 'adjustment' && adjustmentSign === -1);
  const onHand = item?.onHand ?? 0;
  const exceedsStock = drawsDownStock && quantity != null && quantity > onHand;

  const handleClientChange = useCallback((selection: Client | Client[] | undefined) => {
    const client = Array.isArray(selection) ? selection[0] : selection;
    setSelectedClient(client ?? null);
    setRecipientId(client?.clientId ?? null);
    setError('');
  }, []);

  const handleCaregiverChange = useCallback((selection: CaregiverMinified | CaregiverMinified[] | undefined) => {
    const caregiver = Array.isArray(selection) ? selection[0] : selection;
    setSelectedCaregiver(caregiver ?? null);
    setRecipientId(caregiver?.caregiverId ?? null);
    setError('');
  }, []);

  // Client-side validation mirroring the backend `validate_movement`; returns an
  // error message key result (localized) or null when the form is valid.
  const validate = (): string | null => {
    if (quantity == null || quantity <= 0) return t('inventory-validation-quantity-required');
    if (exceedsStock) return t('inventory-validation-insufficient-stock', { available: onHand });

    if (movementType === 'purchase' && (unitCost == null || unitCost < 0)) {
      return t('inventory-validation-unit-cost-required');
    }
    if (movementType === 'sale' && (unitPrice == null || unitPrice < 0)) {
      return t('inventory-validation-unit-price-required');
    }
    if (movementType === 'rental_out') {
      if (rentalRate == null || rentalRate < 0) return t('inventory-validation-rental-rate-required');
      if (!rentalStart) return t('inventory-validation-rental-start-required');
      if (!rentalExpectedReturn) return t('inventory-validation-rental-expected-return-required');
      if (rentalStart && rentalExpectedReturn && rentalExpectedReturn.isBefore(rentalStart, 'day')) {
        return t('inventory-validation-rental-return-before-start');
      }
    }
    if (movementType === 'adjustment' && adjustmentSign == null) {
      return t('inventory-validation-adjustment-sign-required');
    }

    if (showsRecipient) {
      if (recipientType === 'client') {
        if (recipientId == null) return t('inventory-validation-recipient-client-required');
      } else if (recipientType === 'caregiver') {
        if (recipientId == null) return t('inventory-validation-recipient-caregiver-required');
      } else if (recipientType === 'external') {
        if (!recipientName.trim()) return t('inventory-validation-recipient-name-required');
      }
    }

    return null;
  };

  const handleSave = async () => {
    if (item == null) return;

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    // Build the create body with only the fields relevant to the chosen type.
    const body: InventoryMovementCreate = {
      itemId: item.itemId,
      movementType,
      quantity: quantity as number,
    };

    if (movementType === 'purchase') {
      body.unitCost = unitCost;
      if (supplier.trim()) body.supplier = supplier.trim();
    } else if (movementType === 'sale') {
      body.unitPrice = unitPrice;
    } else if (movementType === 'give') {
      if (visitId != null) body.visitId = visitId;
    } else if (movementType === 'rental_out') {
      body.rentalRate = rentalRate;
      body.rentalRateUnit = rentalRateUnit;
      body.rentalStart = rentalStart ? rentalStart.format(DATE_FORMAT) : null;
      body.rentalExpectedReturn = rentalExpectedReturn ? rentalExpectedReturn.format(DATE_FORMAT) : null;
    } else if (movementType === 'adjustment') {
      body.adjustmentSign = adjustmentSign;
    }

    if (showsRecipient) {
      body.recipientType = recipientType;
      if (recipientType === 'client' || recipientType === 'caregiver') {
        body.recipientId = recipientId;
      } else if (recipientType === 'external') {
        body.recipientName = recipientName.trim();
      }
    }

    if (notes.trim()) body.notes = notes.trim();
    if (occurredAt) body.occurredAt = occurredAt.format(DATE_FORMAT);

    try {
      setIsSaving(true);
      await createMovement(body);
      message.success(t('inventory-movement-success'));
      onSaved();
      onClose();
    } catch (e) {
      handleError(e as Error, { customMessage: t('inventory-movement-failed') });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('inventory-record-movement-title')}
      onCancel={isSaving ? undefined : onClose}
      onOk={handleSave}
      okText={t('common-save')}
      cancelText={t('common-cancel')}
      confirmLoading={isSaving}
      maskClosable={!isSaving}
      destroyOnHidden
    >
      <div className="flex flex-col gap-3 pt-2">
        <FormElement label={t('inventory-field-movement-type')} required>
          <Select<InventoryMovementType>
            className="w-full"
            value={movementType}
            disabled={isSaving}
            onChange={(value) => {
              setMovementType(value);
              setError('');
            }}
            options={MOVEMENT_TYPES.map((type) => ({ value: type, label: t(`inventory-mtype-${type}`) }))}
          />
        </FormElement>

        <FormElement label={t('inventory-mcol-quantity')} required>
          <InputNumber
            className="w-full"
            min={0}
            // Capped rather than merely validated, so the spinner and paste
            // clamp instead of letting the user build an invalid number first.
            max={drawsDownStock ? onHand : undefined}
            status={exceedsStock ? 'error' : undefined}
            value={quantity}
            disabled={isSaving}
            onChange={(value) => {
              setQuantity(value ?? null);
              setError('');
            }}
          />
          {drawsDownStock && (
            <div className={`mt-1 text-xs ${exceedsStock ? 'text-red-500' : 'text-gray-500'}`}>
              {t('inventory-stock-on-hand-hint', { available: onHand })}
            </div>
          )}
        </FormElement>

        {movementType === 'purchase' && (
          <>
            <FormElement label={t('inventory-field-unit-cost')} required>
              <InputNumber
                className="w-full"
                min={0}
                value={unitCost}
                disabled={isSaving}
                onChange={(value) => {
                  setUnitCost(value ?? null);
                  setError('');
                }}
              />
            </FormElement>
            <FormElement label={t('inventory-field-supplier')}>
              <Input value={supplier} disabled={isSaving} onChange={(e) => setSupplier(e.target.value)} />
            </FormElement>
          </>
        )}

        {movementType === 'sale' && (
          <FormElement label={t('inventory-field-unit-price')} required>
            <InputNumber
              className="w-full"
              min={0}
              value={unitPrice}
              disabled={isSaving}
              onChange={(value) => {
                setUnitPrice(value ?? null);
                setError('');
              }}
            />
          </FormElement>
        )}

        {movementType === 'give' && (
          <FormElement label={t('inventory-field-visit-id')}>
            <InputNumber
              className="w-full"
              min={0}
              value={visitId}
              disabled={isSaving}
              onChange={(value) => setVisitId(value ?? null)}
            />
          </FormElement>
        )}

        {movementType === 'rental_out' && (
          <>
            <FormElement label={t('inventory-field-rental-rate')} required>
              <InputNumber
                className="w-full"
                min={0}
                value={rentalRate}
                disabled={isSaving}
                onChange={(value) => {
                  setRentalRate(value ?? null);
                  setError('');
                }}
              />
            </FormElement>
            <FormElement label={t('inventory-field-rental-rate-unit')} required>
              <Select<RentalRateUnit>
                className="w-full"
                value={rentalRateUnit}
                disabled={isSaving}
                onChange={(value) => setRentalRateUnit(value)}
                options={RENTAL_RATE_UNITS.map((unit) => ({
                  value: unit,
                  label: t(`inventory-rental-rate-unit-${unit}`),
                }))}
              />
            </FormElement>
            <FormElement label={t('inventory-field-rental-start')} required>
              <DatePicker
                className="w-full"
                value={rentalStart}
                disabled={isSaving}
                onChange={(value) => {
                  setRentalStart(value);
                  setError('');
                }}
              />
            </FormElement>
            <FormElement label={t('inventory-field-rental-expected-return')} required>
              <DatePicker
                className="w-full"
                value={rentalExpectedReturn}
                disabled={isSaving}
                disabledDate={(current) => !!rentalStart && current && current < rentalStart.startOf('day')}
                onChange={(value) => {
                  setRentalExpectedReturn(value);
                  setError('');
                }}
              />
            </FormElement>
          </>
        )}

        {movementType === 'adjustment' && (
          <FormElement label={t('inventory-field-adjustment-sign')} required>
            <Radio.Group
              value={adjustmentSign}
              disabled={isSaving}
              onChange={(e) => {
                setAdjustmentSign(e.target.value as number);
                setError('');
              }}
            >
              <Radio value={1}>{t('inventory-adjustment-increase')}</Radio>
              <Radio value={-1}>{t('inventory-adjustment-decrease')}</Radio>
            </Radio.Group>
          </FormElement>
        )}

        {showsRecipient && (
          <>
            <FormElement label={t('inventory-field-recipient-type')} required>
              <Select<InventoryRecipientType>
                className="w-full"
                value={recipientType}
                disabled={isSaving}
                onChange={(value) => {
                  setRecipientType(value);
                  // Drop the previous pick: recipientId is polymorphic, so a
                  // leftover client id would otherwise be sent as a caregiver id.
                  setRecipientId(null);
                  setSelectedClient(null);
                  setSelectedCaregiver(null);
                  setRecipientName('');
                  setError('');
                }}
                options={RECIPIENT_TYPES.map((type) => ({
                  value: type,
                  label: t(`inventory-recipient-type-${type}`),
                }))}
              />
            </FormElement>

            {recipientType === 'client' && (
              <FormElement label={t('inventory-field-recipient-client')} required>
                <ClientSelectGeneric
                  className="w-full"
                  allowClear
                  disabled={isSaving}
                  value={recipientId ?? undefined}
                  onClientChange={handleClientChange}
                  selectedItemsProvider={() => selectedClient ?? undefined}
                />
              </FormElement>
            )}

            {recipientType === 'caregiver' && (
              <FormElement label={t('inventory-field-recipient-caregiver')} required>
                <CaregiversSelectGeneric
                  className="w-full"
                  allowClear
                  disabled={isSaving}
                  value={recipientId ?? undefined}
                  onCaregiverChange={handleCaregiverChange}
                  selectedItemsProvider={() => selectedCaregiver ?? undefined}
                  lazyLoadOnOpen
                />
              </FormElement>
            )}

            {recipientType === 'external' && (
              <FormElement label={t('inventory-field-recipient-name')} required>
                <Input
                  value={recipientName}
                  disabled={isSaving}
                  onChange={(e) => {
                    setRecipientName(e.target.value);
                    setError('');
                  }}
                />
              </FormElement>
            )}
          </>
        )}

        {showsChargeInfo && <Alert type="info" showIcon message={t('inventory-movement-charge-info')} />}

        <FormElement label={t('inventory-field-occurred-at')}>
          <DatePicker
            className="w-full"
            value={occurredAt}
            disabled={isSaving}
            disabledDate={(current) => current && current > dayjs().endOf('day')}
            onChange={(value) => setOccurredAt(value)}
          />
        </FormElement>

        <FormElement label={t('inventory-mcol-note')}>
          <Input.TextArea rows={3} value={notes} disabled={isSaving} onChange={(e) => setNotes(e.target.value)} />
        </FormElement>

        {error && <div className="text-red-500 text-xs">{error}</div>}
      </div>
    </Modal>
  );
};

export default RecordMovementModal;
